import {
  FC,
  PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Skeleton } from '@mui/material';
import { createContext, useContext } from 'use-context-selector';

import { useUserPaymentContext } from './UserPaymentProvider';
import { useUserSubscriptionContext } from './UserSubscriptionProvider';

import { PAYMENT_GATEWAY, PAYMENT_STEP } from '~common';
import { useStationPricingContext } from '~providers';
import { CampusCardSummary, PaymentInfo } from '~types';
import { Pricing } from '~types/pricing.type';
import { SubscriptionStatus } from '~types/subscription.type';

// A linked campus card chosen as the rental payment method. Tagged so it's
// unambiguously distinct from a Stripe `PaymentInfo` and the 'WALLET' sentinel.
type SelectedCampusCard = {
  kind: PAYMENT_GATEWAY.CAMPUS_CARD;
  card: CampusCardSummary;
};

type Payment = PaymentInfo | 'WALLET' | SelectedCampusCard;
type PaymentMethodToDelete = PaymentInfo | CampusCardSummary;

export const isSelectedCampusCard = (
  payment: Payment | null,
): payment is SelectedCampusCard =>
  typeof payment === 'object' && payment !== null && 'kind' in payment;

type PaymentStepContextType = {
  paymentStep: PAYMENT_STEP;
  handleSetPaymentStep: (paymentStep: PAYMENT_STEP) => void;
  selectedPaymentMethod: Payment | null;
  handleSetSelectedPaymentMethod: (paymentMethod: Payment) => void;
  selectedPaymentMethodToDelete: PaymentMethodToDelete | null;
  handleSetSelectedPaymentMethodToDelete: (
    paymentMethod: PaymentMethodToDelete | null,
  ) => void;
  selectedPricing: Pricing | null;
  handleSetSelectedPricing: (pricing: Pricing) => void;
};

const PaymentStepContext = createContext<PaymentStepContextType>({
  paymentStep: PAYMENT_STEP.SELECT_PAYMENT_METHOD,
  handleSetPaymentStep: () => {
    return;
  },
  selectedPaymentMethod: null,
  handleSetSelectedPaymentMethod: () => {
    return;
  },
  selectedPaymentMethodToDelete: null,
  handleSetSelectedPaymentMethodToDelete: () => {
    return;
  },
  selectedPricing: null,
  handleSetSelectedPricing: () => {
    return;
  },
});

export const PaymentStepProvider: FC<PropsWithChildren> = ({ children }) => {
  const { paymentInfo } = useUserPaymentContext();
  const {
    activeSubscription,
    isLoadingActiveSubscription,
    subscriptionStatus,
  } = useUserSubscriptionContext();
  const { hasMultiplePricingOptions, allowSubscription } =
    useStationPricingContext();

  const hasSubscription = !!activeSubscription;
  const isHavingActiveRental = !!activeSubscription?.isHavingActiveRental;

  const [paymentStep, setPaymentStep] = useState<PAYMENT_STEP>(
    PAYMENT_STEP.RENT_A_CHARGER,
  );

  const handleSetPaymentStep = useCallback((paymentStep: PAYMENT_STEP) => {
    setPaymentStep(paymentStep);
  }, []);

  const [selectedPaymentMethod, setSelectedPaymentMethod] =
    useState<Payment | null>(null);
  const [selectedPaymentMethodToDelete, setSelectedPaymentMethodToDelete] =
    useState<PaymentMethodToDelete | null>(null);
  const [selectedPricing, setSelectedPricing] = useState<Pricing | null>(null);

  const handleSetSelectedPaymentMethod = useCallback(
    (paymentMethod: Payment) => {
      setSelectedPaymentMethod(paymentMethod);
      setPaymentStep(PAYMENT_STEP.RENT_A_CHARGER);
    },
    [],
  );

  const handleSetSelectedPricing = useCallback((pricing: Pricing) => {
    setSelectedPricing(pricing);
  }, []);

  const handleSetSelectedPaymentMethodToDelete = useCallback(
    (paymentMethod: PaymentMethodToDelete | null) => {
      setSelectedPaymentMethodToDelete(paymentMethod);
    },
    [],
  );

  useEffect(() => {
    // Any saved card is pre-selectable, including cards saved through a wallet
    // flow (method APPLE_PAY/GOOGLE_PAY); only fall back to the wallet row when
    // the user has no saved card at all.
    if (paymentInfo) {
      setSelectedPaymentMethod(paymentInfo);
    } else {
      setSelectedPaymentMethod('WALLET');
    }
  }, [paymentInfo]); // only run when the payment info changes

  useEffect(() => {
    // Pay-per-use: the pricing picker when there is something to pick between,
    // otherwise straight to checkout.
    const payPerUseStep = hasMultiplePricingOptions
      ? PAYMENT_STEP.SELECT_PRICING_MODELS
      : PAYMENT_STEP.RENT_A_CHARGER;

    // `RENEWAL_PENDING` and `NOT_USABLE_AT_STATION` both mean the pass cannot
    // be redeemed right now — offer pay-per-use meanwhile. `PassStatusBanner`
    // explains why on top of whichever step this lands on.
    if (subscriptionStatus !== SubscriptionStatus.USABLE) {
      if (payPerUseStep === PAYMENT_STEP.SELECT_PRICING_MODELS) {
        setPaymentStep(payPerUseStep);
      }
      return;
    }

    if (!hasSubscription || isHavingActiveRental) {
      return;
    }

    // If the station doesn't allow the subscription flow, force users into the
    // non-subscription pricing flow.
    if (!allowSubscription) {
      setPaymentStep(payPerUseStep);
      return;
    }

    setPaymentStep(PAYMENT_STEP.AVAILABLE_SWAPS);
    // Depend on primitives, never on `activeSubscription` itself: react-query
    // hands back a fresh object on every refetch, and depending on its identity
    // made this effect re-run and slam the step back to AVAILABLE_SWAPS —
    // tearing the user off the pass screen mid-flow and destroying the
    // cancel/resubscribe confirmation before they could read it.
  }, [
    hasSubscription,
    isHavingActiveRental,
    subscriptionStatus,
    hasMultiplePricingOptions,
    allowSubscription,
  ]);

  const providerValues = useMemo(
    () => ({
      paymentStep,
      handleSetPaymentStep,
      selectedPaymentMethod,
      handleSetSelectedPaymentMethod,
      selectedPaymentMethodToDelete,
      handleSetSelectedPaymentMethodToDelete,
      selectedPricing,
      handleSetSelectedPricing,
    }),
    [
      paymentStep,
      handleSetPaymentStep,
      selectedPaymentMethod,
      handleSetSelectedPaymentMethod,
      selectedPaymentMethodToDelete,
      handleSetSelectedPaymentMethodToDelete,
      selectedPricing,
      handleSetSelectedPricing,
    ],
  );

  if (isLoadingActiveSubscription) {
    return (
      <>
        <Skeleton variant="rectangular" height={100} />
        <Skeleton variant="rectangular" height={100} />
        <Skeleton variant="rectangular" height={100} />
      </>
    );
  }

  return (
    <PaymentStepContext.Provider value={providerValues}>
      {children}
    </PaymentStepContext.Provider>
  );
};

export const usePaymentStepContext = () => useContext(PaymentStepContext);
